// frontend/src/pages/SystemJobs.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import { isChairmanReadOnly } from '../utils/access';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/system-jobs';

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const JOBS = [
  {
    key:      'mdn_generation',
    endpoint: '/run-mdn',
    title:    '📋 MDN Generation',
    desc:     'Creates Monthly Debit Notes for all active members — subscription, ROSCA, savings, and loan instalments.',
    color:    '#2980b9',
    warning:  'This will post MDN entries to MIFL for ALL active members for the current month. Skips members who already have an MDN this month.',
  },
  {
    key:      'interest_posting',
    endpoint: '/run-interest',
    title:    '💰 Loan Interest Posting',
    desc:     'Posts monthly interest charges to MIFL for all active loans based on each loan\'s interest rate.',
    color:    '#e67e22',
    warning:  'This will post interest entries to MIFL for ALL active loans. Skips loans already posted this month.',
  },
  {
    key:      'rosca_fines',
    endpoint: '/run-rosca-fines',
    title:    '⚠ ROSCA Fine Generation',
    desc:     'Generates Tier 1 fines (10%) for all unpaid ROSCA contributions from previous months.',
    color:    '#8e44ad',
    warning:  'This will create fine records for ALL members with unpaid ROSCA contributions from past months. Skips contributions already fined.',
  },
  {
    key:      'loan_reminders',
    endpoint: '/run-loan-reminders',
    title:    '⏰ Loan Payment Reminders',
    desc:     'Sends a WhatsApp reminder for every active loan due within the next 3 days. Also runs automatically every day at 08:00 — this button is for testing or catching up.',
    color:    '#16a085',
    warning:  'This will send a WhatsApp reminder to every member with an active loan due in the next 3 days. Each loan is only reminded once, regardless of how many times you click this.',
  },
  {
    key:      'payment_advice_reminders',
    endpoint: '/run-payment-advice-reminders',
    title:    '📨 Payment Advice Slip Reminders',
    desc:     "Auto-generates and sends this period's Payment Advice Slip to every active member who doesn't already have one, 3 days before the association's due date. Also runs automatically every day at 08:00 — this button is for testing.",
    color:    '#2471a3',
    warning:  "Outside the 3-day window before the due date, this does nothing (reports how many days remain). Inside it, this sends a Payment Advice Slip to every active member without one yet this period. Safe to click more than once — members who already have a slip this period are skipped.",
  },
];

function JobCard({ job, lastRun, onRun }) {
  const [running,   setRunning]   = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [confirm,   setConfirm]   = useState(false);
  const readOnly = isChairmanReadOnly();

  const handleRun = async () => {
    if (!confirm) { setConfirm(true); return; }
    setConfirm(false);
    setRunning(true); setResult(null); setError('');
    try {
      const res = await axios.post(`${API}${job.endpoint}`, {}, { headers: getHeaders() });
      setResult(res.data);
      onRun();
    } catch (err) {
      setError(err.response?.data?.error || 'Job failed');
    }
    setRunning(false);
  };

  const cancelConfirm = () => setConfirm(false);

  return (
    <div style={{
      background: 'white', border: `1px solid #e8e8e8`,
      borderLeft: `5px solid ${job.color}`,
      borderRadius: 8, padding: 24, marginBottom: 20,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>{job.title}</div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 8, lineHeight: 1.5 }}>{job.desc}</div>
          <div style={{ fontSize: 12, color: '#999' }}>
            Last run: {lastRun ? (
              <>
                <strong style={{ color: '#555' }}>{new Date(lastRun.last_run).toLocaleString('en-GB')}</strong>
                {lastRun.last_result && (() => {
                  try {
                    const r = JSON.parse(lastRun.last_result);
                    return <span style={{ marginLeft: 8, color: job.color }}>— {r.created} created, {r.skipped} skipped</span>;
                  } catch { return null; }
                })()}
              </>
            ) : <span style={{ color: '#bbb' }}>Never run</span>}
          </div>
        </div>

        {/* Action button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {!readOnly && (!confirm ? (
            <button onClick={handleRun} disabled={running}
              style={{
                background: running ? '#95a5a6' : job.color,
                color: 'white', border: 'none',
                padding: '10px 24px', borderRadius: 6,
                cursor: running ? 'not-allowed' : 'pointer',
                fontSize: 14, fontWeight: 700,
                minWidth: 130
              }}>
              {running ? '⏳ Running...' : '▶ Run Now'}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#e74c3c', fontWeight: 600 }}>Confirm?</span>
              <button onClick={handleRun} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Yes, Run</button>
              <button onClick={cancelConfirm} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            </div>
          ))}
        </div>
      </div>

      {/* Warning notice */}
      {confirm && (
        <div style={{ background: '#fff9e6', border: '1px solid #f39c12', borderRadius: 6, padding: '10px 14px', marginTop: 12, fontSize: 13, color: '#856404' }}>
          ⚠ {job.warning}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: '#fff5f5', border: '1px solid #e74c3c', borderRadius: 6, padding: '10px 14px', marginTop: 12, fontSize: 13, color: '#c0392b' }}>
          ✕ {error}
        </div>
      )}

      {/* Result summary */}
      {result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              { label: 'Created',  val: result.created, color: '#27ae60' },
              { label: 'Skipped',  val: result.skipped, color: '#f39c12' },
              { label: 'Total',    val: result.total,   color: '#2c3e50' },
              { label: 'Period',   val: result.period,  color: job.color  },
            ].map((s, i) => (
              <div key={i} style={{ background: s.color + '15', border: `1px solid ${s.color}40`, borderRadius: 6, padding: '8px 14px', textAlign: 'center', minWidth: 80 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {result.details?.length > 0 && (
            <>
              <button onClick={() => setShowDetail(!showDetail)}
                style={{ background: 'none', border: `1px solid ${job.color}`, color: job.color, padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                {showDetail ? '▲ Hide Details' : '▼ Show Details'}
              </button>
              {showDetail && (
                <div style={{ marginTop: 10, maxHeight: 280, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f5f7fa', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Member</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Status</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.details.map((d, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fafafa' : 'white' }}>
                          <td style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{d.member}</td>
                          <td style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0' }}>
                            <span style={{
                              background: d.status === 'created' || d.status === 'posted' ? '#d5f5e3' : '#fef9e7',
                              color: d.status === 'created' || d.status === 'posted' ? '#1e8449' : '#856404',
                              padding: '1px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600
                            }}>{d.status?.toUpperCase()}</span>
                          </td>
                          <td style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0', color: '#555' }}>
                            {d.reason || (d.obligations ? d.obligations.join(', ') : '') ||
                             (d.interest ? `Interest: UGX ${Number(d.interest).toLocaleString()} @ ${d.rate}%` : '') ||
                             (d.fine ? `Fine: UGX ${Number(d.fine).toLocaleString()} (Period ${d.period})` : '') || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BackupCard() {
  const [info,    setInfo]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');
  const readOnly = isChairmanReadOnly();
  const color = '#117a65';

  const load = async () => {
    try {
      const res = await axios.get(`${API}/backups`, { headers: getHeaders() });
      setInfo(res.data); setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load backups');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const runNow = async (emailCopy = false) => {
    setRunning(true); setResult(null); setError('');
    try {
      const res = await axios.post(`${API}/run-backup`, { email_copy: emailCopy }, { headers: getHeaders() });
      setResult(res.data);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Backup failed');
    }
    setRunning(false);
  };

  const fmtSize = (b) => b > 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB';
  let last = null;
  try { last = info?.last ? { at: info.last.last_run, ...JSON.parse(info.last.last_result) } : null; } catch { last = null; }
  const files = (info?.backups || []).filter(b => b.key.endsWith('.json.gz'));

  return (
    <div style={{ background: 'white', border: '1px solid #e8e8e8', borderLeft: `5px solid ${color}`, borderRadius: 8, padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>🛡 Database Backup</div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 8, lineHeight: 1.5 }}>
            Backs up the whole database to Cloudflare R2 automatically every night at 02:00. The last 30 nights are kept, plus a permanent copy on the 10th of every month, which is also emailed to the Association Gmail. Use Backup Now before month-end allocations or payouts.
          </div>
          <div style={{ fontSize: 12, color: '#999' }}>
            Last backup: {last ? (
              <>
                <strong style={{ color: '#555' }}>{new Date(last.at).toLocaleString('en-GB')}</strong>
                {last.ok
                  ? <span style={{ marginLeft: 8, color }}>— {last.rows} rows, {fmtSize(last.bytes)} ({last.trigger})</span>
                  : <span style={{ marginLeft: 8, color: '#c0392b' }}>— FAILED: {last.error}</span>}
                {last.ok && last.email?.configured && (last.email.ok
                  ? <span style={{ marginLeft: 8, color }}>· Email sent{last.email.attached ? '' : ' (not attached — too large)'} ✓</span>
                  : <span style={{ marginLeft: 8, color: '#c0392b' }}>· Email FAILED: {last.email.error}</span>)}
              </>
            ) : <span style={{ color: '#bbb' }}>None yet</span>}
          </div>
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
            <button onClick={() => runNow(false)} disabled={running || (info && !info.configured)}
              style={{ background: (running || (info && !info.configured)) ? '#95a5a6' : color, color: 'white', border: 'none', padding: '10px 24px', borderRadius: 6, cursor: running ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, minWidth: 130 }}>
              {running ? '⏳ Backing up...' : '▶ Backup Now'}
            </button>
            {info && info.configured && info.email_configured && (
              <button onClick={() => runNow(true)} disabled={running}
                style={{ background: 'white', color, border: `1px solid ${color}`, padding: '8px 24px', borderRadius: 6, cursor: running ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                ✉ Backup + Email Copy
              </button>
            )}
          </div>
        )}
      </div>

      {info && !info.configured && (
        <div style={{ background: '#fff9e6', border: '1px solid #f39c12', borderRadius: 6, padding: '10px 14px', marginTop: 12, fontSize: 13, color: '#856404' }}>
          ⚠ Backups are OFF — Cloudflare R2 is not connected yet. Set R2_ACCOUNT_ID, R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY and R2_BUCKET on Render.
        </div>
      )}
      {info && info.configured && !info.email_configured && (
        <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: 6, padding: '10px 14px', marginTop: 12, fontSize: 13, color: '#1a5276' }}>
          ℹ Monthly email copy is not connected yet — backups are stored on Cloudflare R2 only. Set GMAIL_USER and GMAIL_APP_PASSWORD on Render to turn it on.
        </div>
      )}
      {error && (
        <div style={{ background: '#fff5f5', border: '1px solid #e74c3c', borderRadius: 6, padding: '10px 14px', marginTop: 12, fontSize: 13, color: '#c0392b' }}>✕ {error}</div>
      )}
      {result && (
        <div style={{ background: '#eafaf1', border: '1px solid #82e0aa', borderRadius: 6, padding: '10px 14px', marginTop: 12, fontSize: 13, color: '#1e8449' }}>
          ✓ Backup saved — {result.tables} tables, {result.rows} rows, {fmtSize(result.bytes)}.{result.email?.configured ? (result.email.ok ? ` Email copy sent to ${result.email.to}.` : ' Email copy FAILED: ' + result.email.error) : ''}
        </div>
      )}

      {loading ? null : files.length > 0 && (
        <div style={{ marginTop: 16, maxHeight: 240, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f5f7fa', position: 'sticky', top: 0 }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>File</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Kind</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Size</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Saved</th>
              </tr>
            </thead>
            <tbody>
              {files.map((b, i) => (
                <tr key={b.key} style={{ background: i % 2 === 0 ? '#fafafa' : 'white' }}>
                  <td style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace' }}>{b.key.split('/')[1]}</td>
                  <td style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0' }}>{b.key.startsWith('monthly/') ? 'Monthly archive' : 'Nightly'}</td>
                  <td style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>{fmtSize(b.size)}</td>
                  <td style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0' }}>{new Date(b.last_modified).toLocaleString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SystemJobs() {
  const [jobStatus, setJobStatus] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/status`, { headers: getHeaders() });
      setJobStatus(res.data);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const getLastRun = (key) => jobStatus.find(j => j.job_name === key) || null;

  const now = new Date();
  const period = now.toLocaleString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto', fontFamily: 'Segoe UI, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, color: '#1a1a2e', fontSize: 24, fontWeight: 700 }}>⚙ System Jobs</h2>
        <p style={{ margin: '6px 0 0', color: '#888', fontSize: 13 }}>
          Manual triggers for monthly financial processing — Current period: <strong style={{ color: '#2c3e50' }}>{period}</strong>
        </p>
      </div>

      {/* Info banner */}
      <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#1a5276' }}>
        ℹ Each job checks for duplicates before running — it is safe to click <strong>Run Now</strong> multiple times.
        Records already processed this month will be skipped automatically.
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#95a5a6' }}>Loading job status...</div>
      ) : (<>
        <BackupCard />
        {JOBS.map(job => (
          <JobCard
            key={job.key}
            job={job}
            lastRun={getLastRun(job.key)}
            onRun={fetchStatus}
          />
        ))}
      </>)}
    </div>
  );
}